import React, { FC, useState } from "react";
import { OverlayView } from "@react-google-maps/api";
import BeamIcon from "../MessageTypes/BeamIcon";
import moment from "moment";
import "./MediaMapMarker.scss";

export type MediaMarkerKind = "image" | "video" | "audio" | "file";

export interface MediaMarkerData {
  id: string;
  lat: number;
  lng: number;
  objectType?: string;
  urls?: {
    original?: { url?: string };
    thumbnail?: { url?: string };
  };
  data?: {
    mimeType?: string;
    name?: string;
  };
  author?: string;
  createdAt?: string | number;
  address?: string;
  tags?: string[];
}

/**
 * Derives the media family from a mime type so the marker can pick the right
 * treatment. Falls back to "file" for anything we cannot preview inline.
 */
export const getMediaKind = (mimeType?: string): MediaMarkerKind => {
  if (!mimeType) return "file";
  if (mimeType.indexOf("image") !== -1) return "image";
  if (mimeType.indexOf("video") !== -1) return "video";
  if (mimeType.indexOf("audio") !== -1) return "audio";
  return "file";
};

const KIND_ICON: Record<MediaMarkerKind, React.ReactNode> = {
  image: <BeamIcon name="image" size={14} />,
  video: <BeamIcon name="video" size={14} />,
  audio: <BeamIcon name="audio" size={14} />,
  file: <BeamIcon name="file" size={14} />,
};

/** Centres the overlay on the pin tip rather than its top-left corner. */
const getPixelPositionOffset = (width: number, height: number) => ({
  x: -(width / 2),
  y: -height,
});

interface MediaMapMarkerProps {
  marker: MediaMarkerData;
  /** Only one marker should be expanded at a time — owned by the parent map. */
  isActive?: boolean;
  onSelect?: (marker: MediaMarkerData) => void;
  onClose?: () => void;
  /** Opens the full media viewer / video modal. */
  onOpenMedia?: (marker: MediaMarkerData) => void;
}

/**
 * A map pin that shows the actual media thumbnail instead of a generic marker.
 *
 * Collapsed it is a small rounded tile; clicking expands it into a card with a
 * larger preview and metadata. Unlike the previous InfoWindow approach this is
 * opt-in per marker, so a map with many media items stays readable.
 */
const MediaMapMarker: FC<MediaMapMarkerProps> = ({
  marker,
  isActive = false,
  onSelect,
  onClose,
  onOpenMedia,
}) => {
  const [errored, setErrored] = useState<boolean>(false);

  const kind = getMediaKind(marker.data?.mimeType);
  const thumbUrl = marker.urls?.thumbnail?.url || marker.urls?.original?.url;
  // Only images render as a real picture; everything else gets an icon tile.
  const showsPicture = kind === "image" && !!thumbUrl && !errored;

  const title = marker.data?.name || "Untitled media";
  const timestamp = marker.createdAt
    ? moment(marker.createdAt).format("MMM D, YYYY h:mm A")
    : null;

  return (
    <OverlayView
      position={{ lat: marker.lat, lng: marker.lng }}
      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
      getPixelPositionOffset={getPixelPositionOffset}
    >
      <div className={`media-map-marker ${isActive ? "is-active" : ""}`}>
        {isActive ? (
          <div className="media-map-marker__card">
            <button
              type="button"
              className="media-map-marker__close"
              onClick={(e) => {
                e.stopPropagation();
                onClose && onClose();
              }}
              aria-label="Close media preview"
            >
              <BeamIcon name="close" size={11} />
            </button>

            <div
              className={`media-map-marker__preview media-map-marker__preview--${kind}`}
              onClick={() => onOpenMedia && onOpenMedia(marker)}
            >
              {showsPicture ? (
                <img
                  src={thumbUrl}
                  alt={title}
                  onError={() => setErrored(true)}
                />
              ) : (
                <span className="media-map-marker__kind-icon">
                  {KIND_ICON[kind]}
                </span>
              )}

              {kind !== "image" && (
                <span className="media-map-marker__play">
                  {KIND_ICON[kind]}
                </span>
              )}

              <span className="media-map-marker__expand">
                <BeamIcon name="expand" size={13} />
              </span>
            </div>

            <div className="media-map-marker__meta">
              <p className="media-map-marker__title" title={title}>
                {title}
              </p>
              {marker.author && (
                <p className="media-map-marker__author">{marker.author}</p>
              )}
              {timestamp && (
                <p className="media-map-marker__time">{timestamp}</p>
              )}
              {marker.address && (
                <p className="media-map-marker__address" title={marker.address}>
                  {marker.address}
                </p>
              )}
              {!!marker.tags?.length && (
                <div className="media-map-marker__tags">
                  {marker.tags.map((tag) => (
                    <span key={tag} className="media-map-marker__tag">
                      #{tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : (
          <button
            type="button"
            className={`media-map-marker__thumb media-map-marker__thumb--${kind}`}
            onClick={() => onSelect && onSelect(marker)}
            aria-label={`Preview ${title}`}
          >
            {showsPicture ? (
              <img
                src={thumbUrl}
                alt={title}
                onError={() => setErrored(true)}
              />
            ) : (
              <span className="media-map-marker__kind-icon">
                {KIND_ICON[kind]}
              </span>
            )}

            {kind !== "image" && (
              <span className="media-map-marker__badge">{KIND_ICON[kind]}</span>
            )}
          </button>
        )}

        <span className="media-map-marker__tail" />
      </div>
    </OverlayView>
  );
};

export default MediaMapMarker;
